import React, { useEffect, useRef, useState } from 'react';
import { Loader, MapPin } from 'lucide-react';

const CESIUM_VERSION = '1.136';
const CESIUM_SCRIPT = `https://cesium.com/downloads/cesiumjs/releases/${CESIUM_VERSION}/Build/Cesium/Cesium.js`;
const CESIUM_CSS = `https://cesium.com/downloads/cesiumjs/releases/${CESIUM_VERSION}/Build/Cesium/Widgets/widgets.css`;

const loadCesium = () => new Promise((resolve, reject) => {
  if (window.Cesium) return resolve(window.Cesium);
  window.CESIUM_BASE_URL = `https://cesium.com/downloads/cesiumjs/releases/${CESIUM_VERSION}/Build/Cesium/`;
  if (!document.getElementById('cesium-widgets-css')) {
    const link = document.createElement('link');
    link.id = 'cesium-widgets-css';
    link.rel = 'stylesheet';
    link.href = CESIUM_CSS;
    document.head.appendChild(link);
  }
  const existing = document.getElementById('cesium-script');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.Cesium), { once: true });
    existing.addEventListener('error', () => reject(new Error('Cesium could not be loaded.')), { once: true });
    return;
  }
  const script = document.createElement('script');
  script.id = 'cesium-script';
  script.src = CESIUM_SCRIPT;
  script.onload = () => resolve(window.Cesium);
  script.onerror = () => reject(new Error('Cesium could not be loaded.'));
  document.head.appendChild(script);
});

const pointCoordinates = (point) => {
  const latitude = Number(point?.latitude ?? point?.lat ?? point?.coordinates?.latitude ?? point?.coordinates?.[0]);
  const longitude = Number(point?.longitude ?? point?.lng ?? point?.lon ?? point?.coordinates?.longitude ?? point?.coordinates?.[1]);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null;
};

const CesiumTerrainMap = ({
  points = [],
  legs = [],
  itineraryMetadata = {},
  onMapViewChange,
  manualEditor,
  popupStopEditor,
  stopPlacementEditor,
}) => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const apiKey = process.env.REACT_APP_GOOGLE_MAP_TILES_API_KEY || '';
  const latitude = Number(itineraryMetadata?.coordinates?.latitude) || 38.7223;
  const longitude = Number(itineraryMetadata?.coordinates?.longitude) || -9.1393;

  useEffect(() => {
    let cancelled = false;
    let viewer;
    const initialize = async () => {
      if (!apiKey) {
        setStatus('error');
        setError('Google 3D Tiles is not configured. Add REACT_APP_GOOGLE_MAP_TILES_API_KEY with a domain-restricted Google Maps Tile API key.');
        return;
      }
      try {
        const Cesium = await loadCesium();
        if (cancelled || !containerRef.current) return;
        viewer = new Cesium.Viewer(containerRef.current, {
          animation: false,
          baseLayerPicker: false,
          fullscreenButton: false,
          geocoder: false,
          homeButton: false,
          infoBox: false,
          navigationHelpButton: false,
          sceneModePicker: false,
          selectionIndicator: false,
          timeline: false,
          baseLayer: false,
          skyBox: false,
          skyAtmosphere: false,
        });
        viewerRef.current = viewer;
        const tileset = await Cesium.Cesium3DTileset.fromUrl(
          `https://tile.googleapis.com/v1/3dtiles/root.json?key=${encodeURIComponent(apiKey)}`
        );
        if (cancelled) return;
        viewer.scene.primitives.add(tileset);
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, 2200),
          orientation: {
            heading: 0,
            pitch: Cesium.Math.toRadians(-55),
            roll: 0,
          },
          duration: 0,
        });
        viewer.camera.moveEnd.addEventListener(() => {
          if (!viewer || typeof onMapViewChange !== 'function') return;
          const cartographic = viewer.camera.positionCartographic;
          onMapViewChange({
            center: [Cesium.Math.toDegrees(cartographic.latitude), Cesium.Math.toDegrees(cartographic.longitude)],
            zoom: viewer.camera.positionCartographic.height,
          });
        });
        setStatus('ready');
      } catch (initializationError) {
        if (!cancelled) {
          setStatus('error');
          setError(initializationError.message || 'Google 3D Tiles could not be loaded.');
        }
      }
    };
    initialize();
    return () => {
      cancelled = true;
      viewer?.destroy();
      viewerRef.current = null;
    };
  }, [apiKey, latitude, longitude, onMapViewChange]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !window.Cesium) return undefined;
    const Cesium = window.Cesium;
    viewer.entities.removeAll();
    points.forEach((point, index) => {
      const coordinates = pointCoordinates(point);
      if (!coordinates) return;
      viewer.entities.add({
        id: `guide-stop-${point.id || index}`,
        position: Cesium.Cartesian3.fromDegrees(coordinates[1], coordinates[0], 20),
        point: {
          pixelSize: 12,
          color: Cesium.Color.fromCssColorString('#2563eb'),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        },
        label: {
          text: String(point.name || point.title || `Stop ${index + 1}`),
          font: '14px sans-serif',
          fillColor: Cesium.Color.WHITE,
          showBackground: true,
          backgroundColor: Cesium.Color.fromCssColorString('#0f172a').withAlpha(0.8),
          pixelOffset: new Cesium.Cartesian2(0, -22),
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        },
      });
    });
    legs.forEach((leg, legIndex) => {
      const coordinates = (Array.isArray(leg?.geometry?.coordinates) ? leg.geometry.coordinates : leg?.points || [])
        .map((coordinate) => Array.isArray(coordinate) ? [Number(coordinate[1]), Number(coordinate[0])] : pointCoordinates(coordinate))
        .filter(Boolean);
      if (coordinates.length < 2) return;
      viewer.entities.add({
        id: `guide-leg-${leg.id || legIndex}`,
        polyline: {
          positions: Cesium.Cartesian3.fromDegreesArray(coordinates.flatMap(([lat, lng]) => [lng, lat])),
          width: 5,
          material: Cesium.Color.fromCssColorString('#7c3aed'),
          clampToGround: true,
        },
      });
    });
  }, [points, legs]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !window.Cesium) return undefined;
    const Cesium = window.Cesium;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction((movement) => {
      const cartesian = viewer.camera.pickEllipsoid(movement.position, viewer.scene.globe.ellipsoid);
      if (!cartesian) return;
      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      const location = [Cesium.Math.toDegrees(cartographic.latitude), Cesium.Math.toDegrees(cartographic.longitude)];
      if (manualEditor?.isActive) manualEditor.onMapClick?.(location);
      else if (popupStopEditor?.isActive) popupStopEditor.onMapClick?.(location);
      else if (stopPlacementEditor?.isActive) stopPlacementEditor.onMapClick?.(location);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    return () => handler.destroy();
  }, [manualEditor, popupStopEditor, stopPlacementEditor]);

  return (
    <div className="relative h-full min-h-[500px] w-full overflow-hidden rounded-b-lg bg-slate-900">
      <div ref={containerRef} className="absolute inset-0" />
      {status === 'loading' && <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 text-white"><Loader className="mr-2 h-5 w-5 animate-spin" />Loading Google 3D terrain…</div>}
      {status === 'error' && <div className="absolute left-4 right-4 top-4 rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900 shadow-lg"><div className="font-semibold">Google 3D terrain unavailable</div><div className="mt-1">{error}</div></div>}
      {status === 'ready' && <div className="pointer-events-none absolute bottom-2 right-2 rounded bg-white/85 px-2 py-1 text-[10px] text-slate-700">Google 3D Tiles · satellite and elevation</div>}
    </div>
  );
};

export default CesiumTerrainMap;
